// /admin/health — system X-ray dashboard. Surfaces pg-boss queue
// depths, image-gen failures, letter pipeline health (fallbacks +
// high-retry), cost outliers, and bond cohort activity. Distinct from
// /admin/cost (which covers granular cost breakdowns); health answers
// "is anything broken / stuck / going dark", not "where the money
// goes".
//
// All data from the single GET /api/admin/health endpoint — one round
// trip, ~10 parallel SQL queries server-side.

import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { type AdminHealth, getAdminHealth } from '../lib/api';

export const Route = createFileRoute('/admin/health')({
  loader: async () => {
    const health = await getAdminHealth();
    return { health };
  },
  component: HealthPage,
});

function fmtUsd(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

function fmtAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function HealthPage() {
  const { health } = Route.useLoaderData();
  const router = useRouter();
  const [refreshing, setRefreshing] = useState(false);

  async function refresh() {
    setRefreshing(true);
    try {
      await router.invalidate();
    } finally {
      setRefreshing(false);
    }
  }

  const queueActiveTotal = health.queue.byJob.reduce(
    (acc, j) => acc + j.active + j.created + j.retry,
    0,
  );
  const imgRate24h = health.imageGen.last24h.completed + health.imageGen.last24h.failed;
  const letterFallbackRate24h =
    health.letters.last24h.delivered === 0
      ? 0
      : Math.round((health.letters.last24h.fallback / health.letters.last24h.delivered) * 100);

  return (
    <section className="space-y-10">
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ash-50 tracking-wide">System health</h2>
          <p className="mt-1 font-body text-ash-300 text-sm">
            Queue depths, image-gen pipeline, letter pipeline, cost outliers, bond cohort.
            Pull manually — no auto-refresh.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="rounded-sm border border-accent/40 bg-paper-900/40 px-3 py-1.5 font-display text-[0.62rem] text-accent uppercase tracking-[0.34em] transition hover:bg-paper-800/60 disabled:opacity-40"
        >
          {refreshing ? '…' : 'Refresh'}
        </button>
      </header>

      {/* Topline cards — at-a-glance numbers */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ToplineCard
          label="Queue (in flight)"
          value={String(queueActiveTotal)}
          sub={`${health.queue.byJob.length} queues`}
        />
        <ToplineCard
          label="Image-gen 24h"
          value={`${health.imageGen.last24h.completed} / ${health.imageGen.last24h.failed}`}
          sub={imgRate24h === 0 ? 'no activity' : 'ok / failed'}
          tone={health.imageGen.last24h.failed > 0 ? 'warn' : 'ok'}
        />
        <ToplineCard
          label="Letters 24h"
          value={`${health.letters.last24h.delivered} / ${health.letters.last24h.fallback}`}
          sub={letterFallbackRate24h === 0 ? 'delivered / fallback' : `${letterFallbackRate24h}% fallback`}
          tone={letterFallbackRate24h > 10 ? 'warn' : 'ok'}
        />
        <ToplineCard
          label="Bonds"
          value={`${health.bonds.active7d} / ${health.bonds.total}`}
          sub="active 7d / total"
        />
      </div>

      {/* Queue depths */}
      <PanelSection title="Queue depths" subtitle="pg-boss state at this instant">
        {health.queue.byJob.length === 0 ? (
          <EmptyLine text="No queue activity." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full font-body text-ash-200 text-sm">
              <thead>
                <tr className="border-paper-800/60 border-b text-left font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">
                  <th className="py-2 pr-4">Job</th>
                  <th className="py-2 pr-4 text-right">Active</th>
                  <th className="py-2 pr-4 text-right">Pending</th>
                  <th className="py-2 pr-4 text-right">Retry</th>
                  <th className="py-2 pr-4 text-right">Completed 24h</th>
                  <th className="py-2 pr-4 text-right">Failed 24h</th>
                  <th className="py-2 pr-4 text-right">Avg ms (24h)</th>
                </tr>
              </thead>
              <tbody>
                {health.queue.byJob.map((q) => (
                  <tr key={q.name} className="border-paper-800/30 border-b last:border-none">
                    <td className="py-2 pr-4 font-mono text-xs">{q.name}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{q.active}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{q.created}</td>
                    <td
                      className={`py-2 pr-4 text-right tabular-nums ${
                        q.retry > 0 ? 'text-accent-strong' : ''
                      }`}
                    >
                      {q.retry}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums text-ash-100">
                      {q.completed24h}
                    </td>
                    <td
                      className={`py-2 pr-4 text-right tabular-nums ${
                        q.failed24h > 0 ? 'text-accent-strong' : ''
                      }`}
                    >
                      {q.failed24h}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums text-ash-400">
                      {q.avgLatencyMs ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PanelSection>

      {/* Image-gen pipeline */}
      <PanelSection
        title="Image-gen pipeline"
        subtitle={`7d: ${health.imageGen.last7d.completed} completed · ${health.imageGen.last7d.failed} failed`}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <SubPanel title="Top error codes (24h)">
            {health.imageGen.topErrorCodes24h.length === 0 ? (
              <EmptyLine text="No failures." />
            ) : (
              <ul className="space-y-1.5 font-body text-ash-300 text-sm">
                {health.imageGen.topErrorCodes24h.map((e) => (
                  <li key={e.code} className="flex justify-between">
                    <span className="font-mono text-xs">{e.code}</span>
                    <span className="tabular-nums text-ash-500">×{e.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </SubPanel>

          <SubPanel title="Bonds without portrait (10m+)">
            {health.imageGen.bondsWithoutPortrait.length === 0 ? (
              <EmptyLine text="All bonds have portraits." />
            ) : (
              <ul className="space-y-1.5 font-body text-ash-300 text-sm">
                {health.imageGen.bondsWithoutPortrait.map((b) => (
                  <li key={b.bondId} className="flex items-baseline justify-between gap-2">
                    <Link
                      to="/admin/bonds/$id"
                      params={{ id: b.bondId }}
                      className="truncate transition hover:text-ash-50"
                    >
                      {b.emberName}
                    </Link>
                    <span className="flex-shrink-0 font-display text-[0.6rem] text-ash-400">
                      {b.ageHours}h old
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SubPanel>
        </div>
      </PanelSection>

      {/* Letter pipeline */}
      <PanelSection
        title="Letter pipeline"
        subtitle={`7d: ${health.letters.last7d.delivered} delivered · ${health.letters.last7d.fallback} fallback`}
      >
        <SubPanel title="High-retry letters (retry ≥ 2, last 30d)">
          {health.letters.highRetry.length === 0 ? (
            <EmptyLine text="No high-retry letters." />
          ) : (
            <ul className="space-y-1.5 font-body text-ash-300 text-sm">
              {health.letters.highRetry.map((l) => (
                <li
                  key={l.letterId}
                  className="flex items-baseline justify-between gap-2"
                >
                  <Link
                    to="/admin/bonds/$id"
                    params={{ id: l.bondId }}
                    className="truncate transition hover:text-ash-50"
                  >
                    {l.emberName}
                    <span className="ml-2 font-display text-[0.6rem] text-ash-400 uppercase tracking-[0.24em]">
                      {l.letterType}
                    </span>
                  </Link>
                  <span className="flex-shrink-0 font-display text-[0.6rem] text-accent-strong italic">
                    {l.retryCount} retries · {fmtAgo(l.generatedAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SubPanel>
      </PanelSection>

      {/* Cost outliers */}
      <PanelSection
        title="Cost outliers (top 5, 7d)"
        subtitle="High spend signals heavy use or a runaway loop"
      >
        {health.costOutliers.topBonds7d.length === 0 ? (
          <EmptyLine text="No cost telemetry in last 7 days." />
        ) : (
          <ul className="space-y-1.5 font-body text-ash-300 text-sm">
            {health.costOutliers.topBonds7d.map((b) => (
              <li key={b.bondId} className="flex items-baseline justify-between gap-2">
                <Link
                  to="/admin/bonds/$id"
                  params={{ id: b.bondId }}
                  className="truncate transition hover:text-ash-50"
                >
                  {b.emberName}
                  {b.userEmail && (
                    <span className="ml-2 font-body text-[0.7rem] text-ash-400">
                      {b.userEmail}
                    </span>
                  )}
                </Link>
                <span className="flex-shrink-0 font-display text-[0.62rem] text-ash-400 tabular-nums">
                  {fmtUsd(b.totalUsd)} · {b.events} events
                </span>
              </li>
            ))}
          </ul>
        )}
      </PanelSection>

      {/* Stale bonds */}
      <PanelSection
        title="Stale bonds (no activity ≥14d)"
        subtitle="May indicate churn or interest fade"
      >
        {health.bonds.stale.length === 0 ? (
          <EmptyLine text="All bonds active." />
        ) : (
          <ul className="space-y-1.5 font-body text-ash-300 text-sm">
            {health.bonds.stale.map((b) => (
              <li key={b.bondId} className="flex items-baseline justify-between gap-2">
                <Link
                  to="/admin/bonds/$id"
                  params={{ id: b.bondId }}
                  className="truncate transition hover:text-ash-50"
                >
                  {b.emberName}
                </Link>
                <span className="flex-shrink-0 font-display text-[0.6rem] text-ash-400">
                  last {fmtAgo(b.lastActivityAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </PanelSection>
    </section>
  );
}

function ToplineCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'ok' | 'warn';
}) {
  return (
    <div
      className={`rounded-sm border p-4 ${
        tone === 'warn'
          ? 'border-accent-strong/40 bg-paper-900/40'
          : 'border-paper-800/60 bg-paper-900/30'
      }`}
    >
      <p className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
        {label}
      </p>
      <p
        className={`mt-2 font-display text-2xl tabular-nums ${
          tone === 'warn' ? 'text-accent-strong' : 'text-ash-50'
        }`}
      >
        {value}
      </p>
      {sub && (
        <p className="mt-1 font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.28em]">
          {sub}
        </p>
      )}
    </div>
  );
}

function PanelSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header className="mb-4">
        <h3 className="font-display text-[0.66rem] text-ash-400 uppercase tracking-[0.34em]">
          {title}
        </h3>
        {subtitle && (
          <p className="mt-1 font-body text-ash-500 text-xs italic">{subtitle}</p>
        )}
      </header>
      {children}
    </section>
  );
}

function SubPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-sm border border-paper-800/50 bg-paper-900/25 p-4">
      <p className="mb-3 font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">
        {title}
      </p>
      {children}
    </div>
  );
}

function EmptyLine({ text }: { text: string }) {
  return <p className="font-body text-ash-500 text-sm italic">{text}</p>;
}
